import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';
import { useUserStore } from '@/store/UserStore';
import SystemDBList from '@/views/SystemDBList.vue';
import SystemDBEditor from '@/views/SystemDBEditor.vue';
import PublishChanges from '@/views/PublishChanges.vue';
import VersionControl from '@/views/VersionControl.vue';
import QuarantinedList from '@/views/QuarantinedList.vue';
import ProxyTemplatesList from '@/views/ProxyTemplatesList.vue';
import ProxyTemplatesEditor from '@/views/ProxyTemplatesEditor.vue';
import ServerGroupsList from '@/views/ServerGroupsList.vue';
import ServerGroupsEditor from '@/views/ServerGroupsEditor.vue';
import BackendServicesList from '@/views/BackendServicesList.vue';
import BackendServiceEditor from '@/views/BackendServicesEditor.vue';
import HelpAndSupport from '@/views/HelpAndSupport.vue';
import RbzDashboard from '@/views/RbzDashboard.vue';
import DynamicRulesList from '@/views/DynamicRulesList.vue';
import DynamicRulesEditor from '@/views/DynamicRulesEditor.vue';
import EdgeFunctionsList from '@/views/EdgeFunctionsList.vue';
import EdgeFunctionsEditor from '@/views/EdgeFunctionsEditor.vue';
import DnsPage from '@/views/DnsRecords.vue';
import SecurityAlertsList from '@/views/SecurityAlertsList.vue';
import SecurityAlertsEditor from '@/views/SecurityAlertsEditor.vue';
import EventsLog from '@/views/EventsLog.vue';
import UserManagement from '@/views/UsersManagement.vue';
import AccountDetails from '@/views/AccountDetails.vue';
import SsoConfiguration from '@/views/SsoConfiguration.vue';
import PurgeCdnCache from '@/views/PurgeCdnCache.vue';
import InteractiveChallenge from '@/views/InteractiveChallenge.vue';
import GlobalFiltersList from '@/views/GlobalFiltersList.vue';
import GlobalFiltersEditor from '@/views/GlobalFiltersEditor.vue';
import FlowControlPoliciesList from '@/views/FlowControlPoliciesList.vue';
import FlowControlPoliciesEditor from '@/views/FlowControlPoliciesEditor.vue';
import SecurityPoliciesList from '@/views/SecurityPoliciesList.vue';
import SecurityPoliciesEditor from '@/views/SecurityPoliciesEditor.vue';
import RateLimitRulesList from '@/views/RateLimitRulesList.vue';
import AclProfilesList from '@/views/AclProfilesList.vue';
import ActionsList from '@/views/ActionsList.vue';
import ContentFilterProfilesList from '@/views/ContentFilterProfilesList.vue';
import ContentFilterRulesList from '@/views/ContentFilterRulesList.vue';
import ContentFilterRulesEditor from '@/views/ContentFilterRulesEditor.vue';
import ActionsEditor from '@/views/ActionsEditor.vue';
import RateLimitRulesEditor from '@/views/RateLimitRulesEditor.vue';
import ContentFilterProfilesEditor from '@/views/ContentFilterProfilesEditor.vue';
import SslLoadBalancers from '@/views/SslLoadBalancers.vue';
import AclProfilesEditor from '@/views/AclProfilesEditor.vue';
import LogExportersList from '@/views/LogExportersList.vue';
import LogExportersEditor from '@/views/LogExportersEditor.vue';
import MobileApplicationGroupsList from '@/views/MobileApplicationGroupsList.vue';
import MobileApplicationGroupsEditor from '@/views/MobileApplicationGroupsEditor.vue';
import { GenericObject } from '@/types';
import { useBranchesStore } from '@/store/BranchesStore';
import { ENV } from '@/constants';
import CertificatesList from '@/views/CertificatesList.vue';
import AiManagement from '@/views/AiManagement.vue';

const accessLevelGuardEditor = () => {
  const userStore = useUserStore();
  if (!userStore.checkAccessLevel(userStore.accessLevels.waapConfigEditor)) {
    return { path: '/prod/dashboard' };
  }
  return true;
};

const accessLevelGuardOrgAdmin = () => {
  const userStore = useUserStore();
  if (!userStore.checkAccessLevel(userStore.accessLevels.waapManager)) {
    return { path: '/prod/dashboard' };
  }
  return true;
};

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'MainComponent',
    redirect: 'prod/dashboard',
    children: [
      {
        path: ':branch',
        name: 'MainComponent/Branch',
        redirect: (route) => {
          return `/${route.params.branch}/dashboard`;
        },
        children: [
          {
            path: 'dashboard',
            name: 'Dashboard',
            component: RbzDashboard,
          },
          {
            path: 'events-log',
            name: 'Events Log',
            component: EventsLog,
          },
          {
            path: 'ai-management',
            name: 'AI Management',
            component: AiManagement,
          },
          {
            path: 'global-filters',
            name: 'Global Filters',
            redirect: (route) => {
              return `/${route.params.branch}/global-filters/list`;
            },
          },
          {
            path: 'global-filters/list',
            name: 'Global Filters List',
            component: GlobalFiltersList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'global-filters/config/:doc_id',
            name: 'Global Filters Editor',
            component: GlobalFiltersEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'global-filters/new',
            name: 'New Global Filter',
            component: GlobalFiltersEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'flow-control-policies',
            name: 'Flow Control Policies',
            beforeEnter: [accessLevelGuardEditor],
            redirect: (route) => {
              return `/${route.params.branch}/flow-control-policies/list`;
            },
          },
          {
            path: 'flow-control-policies/list',
            name: 'Flow Control Policies List',
            component: FlowControlPoliciesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'flow-control-policies/config/:doc_id',
            name: 'Flow Control Policies Editor',
            component: FlowControlPoliciesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'flow-control-policies/new',
            name: 'New Flow Control Policy',
            component: FlowControlPoliciesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'security-policies',
            name: 'Security Policies',
            redirect: (route) => {
              return `/${route.params.branch}/security-policies/list`;
            },
          },
          {
            path: 'security-policies/list',
            name: 'Security Policies List',
            component: SecurityPoliciesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'security-policies/config/:doc_id',
            name: 'Security Policies Editor',
            component: SecurityPoliciesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'security-policies/new',
            name: 'New Security Policy',
            component: SecurityPoliciesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'rate-limit-rules',
            name: 'Rate Limit Rules',
            redirect: (route) => {
              return `/${route.params.branch}/rate-limit-rules/list`;
            },
          },
          {
            path: 'rate-limit-rules/list',
            name: 'Rate Limit Rules List',
            component: RateLimitRulesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'rate-limit-rules/config/:doc_id',
            name: 'Rate Limit Rules Editor',
            component: RateLimitRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'rate-limit-rules/new',
            name: 'New Rate Limit Rule',
            component: RateLimitRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'acl-profiles',
            name: 'ACL Profiles',
            redirect: (route) => {
              return `/${route.params.branch}/acl-profiles/list`;
            },
          },
          {
            path: 'acl-profiles/list',
            name: 'ACL Profiles List',
            component: AclProfilesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'acl-profiles/config/:doc_id',
            name: 'ACL Profiles Editor',
            component: AclProfilesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'acl-profiles/new',
            name: 'New ACL Profile',
            component: AclProfilesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'actions',
            name: 'Actions',
            redirect: (route) => {
              return `/${route.params.branch}/actions/list`;
            },
          },
          {
            path: 'actions/list',
            name: 'Actions List',
            component: ActionsList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'actions/config/:doc_id',
            name: 'Actions Editor',
            component: ActionsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'actions/new',
            name: 'New Action',
            component: ActionsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'dynamic-rules',
            name: 'Dynamic Rules',
            redirect: (route) => {
              return `/${route.params.branch}/dynamic-rules/list`;
            },
          },
          {
            path: 'dynamic-rules/list',
            name: 'Dynamic Rules List',
            component: DynamicRulesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'dynamic-rules/config/:doc_id',
            name: 'Dynamic Rules Editor',
            component: DynamicRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'dynamic-rules/new',
            name: 'New Dynamic Rule',
            component: DynamicRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'quarantined',
            name: 'Quarantined',
            beforeEnter: [accessLevelGuardEditor],
            component: QuarantinedList,
          },
          {
            path: 'content-filter-profiles',
            name: 'Content Filter Profiles',
            redirect: (route) => {
              return `/${route.params.branch}/content-filter-profiles/list`;
            },
          },
          {
            path: 'content-filter-profiles/list',
            name: 'Content Filter Profiles List',
            component: ContentFilterProfilesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'content-filter-profiles/config/:doc_id',
            name: 'Content Filter Profiles Editor',
            component: ContentFilterProfilesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'content-filter-profiles/new',
            name: 'New Content Filter Profile',
            component: ContentFilterProfilesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'content-filter-rules',
            name: 'Content Filter Rules',
            redirect: (route) => {
              return `/${route.params.branch}/content-filter-rules/list`;
            },
          },
          {
            path: 'content-filter-rules/list',
            name: 'Content Filter Rules List',
            component: ContentFilterRulesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'content-filter-rules/config/:doc_id',
            name: 'Content Filter Rules Editor',
            component: ContentFilterRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'content-filter-rules/new',
            name: 'New Content Filter Rule',
            component: ContentFilterRulesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'server-groups',
            name: 'Server Groups',
            redirect: (route) => {
              return `/${route.params.branch}/server-groups/list`;
            },
          },
          {
            path: 'server-groups/list',
            name: 'Server Groups List',
            component: ServerGroupsList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'server-groups/config/:doc_id',
            name: 'Server Groups Editor',
            component: ServerGroupsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'server-groups/new',
            name: 'New Server Group',
            component: ServerGroupsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'proxy-templates',
            name: 'Proxy Templates',
            redirect: (route) => {
              return `/${route.params.branch}/proxy-templates/list`;
            },
          },
          {
            path: 'proxy-templates/list',
            name: 'Proxy Templates List',
            component: ProxyTemplatesList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'proxy-templates/config/:doc_id',
            name: 'Proxy Templates Editor',
            component: ProxyTemplatesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'proxy-templates/new',
            name: 'New Proxy Template',
            component: ProxyTemplatesEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'mobile-application-groups',
            name: 'Mobile Application Groups',
            redirect: (route) => {
              return `/${route.params.branch}/mobile-application-groups/list`;
            },
          },
          {
            path: 'mobile-application-groups/list',
            name: 'Mobile Application Groups List',
            component: MobileApplicationGroupsList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'mobile-application-groups/config/:doc_id',
            name: 'Mobile Application Groups Editor',
            component: MobileApplicationGroupsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'mobile-application-groups/new',
            name: 'New Mobile Application Group',
            component: MobileApplicationGroupsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'backend-services',
            name: 'Backend Services',
            redirect: (route) => {
              return `/${route.params.branch}/backend-services/list`;
            },
          },
          {
            path: 'backend-services/list',
            name: 'Backend Services List',
            beforeEnter: [accessLevelGuardEditor],
            component: BackendServicesList,
          },
          {
            path: 'backend-services/config/:doc_id',
            name: 'Backend Services Editor',
            beforeEnter: [accessLevelGuardEditor],
            component: BackendServiceEditor,
          },
          {
            path: 'backend-services/new',
            name: 'New Backend Service',
            beforeEnter: [accessLevelGuardEditor],
            component: BackendServiceEditor,
          },
          {
            path: 'edge-functions',
            name: 'Edge Functions',
            redirect: (route) => {
              return `/${route.params.branch}/edge-functions/list`;
            },
          },
          {
            path: 'edge-functions/list',
            name: 'Edge Functions List',
            component: EdgeFunctionsList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'edge-functions/config/:doc_id',
            name: 'Edge Functions Editor',
            component: EdgeFunctionsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'edge-functions/new',
            name: 'New Edge Function',
            component: EdgeFunctionsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'dns-records',
            name: 'DNS Records',
            beforeEnter: [accessLevelGuardEditor],
            component: DnsPage,
          },
          {
            path: 'ssl',
            name: 'SSL',
            beforeEnter: [accessLevelGuardEditor],
            redirect: (route) => {
              return `/${route.params.branch}/load-balancers`;
            },
            children: [
              {
                path: 'load-balancers',
                name: 'Load Balancers',
                component: SslLoadBalancers,
              },
              {
                path: 'certificates',
                name: 'Certificates',
                component: CertificatesList,
              },
            ],
          },
          {
            path: 'interactive-challenge',
            name: 'Interactive Challenge',
            beforeEnter: [accessLevelGuardEditor],
            component: InteractiveChallenge,
          },
          {
            path: 'sso-configuration',
            name: 'SSO Configuration',
            beforeEnter: [accessLevelGuardOrgAdmin],
            component: SsoConfiguration,
          },
          {
            path: 'purge-cdn-cache',
            name: 'Purge CDN Cache',
            beforeEnter: [accessLevelGuardEditor],
            component: PurgeCdnCache,
          },
          {
            path: 'users-management',
            name: 'Users Management',
            beforeEnter: [accessLevelGuardOrgAdmin],
            component: UserManagement,
          },
          {
            path: 'security-alerts',
            name: 'Security Alerts',
            redirect: (route) => {
              return `/${route.params.branch}/security-alerts/list`;
            },
          },
          {
            path: 'security-alerts/list',
            name: 'Security Alerts List',
            component: SecurityAlertsList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'security-alerts/config/:doc_id',
            name: 'Security Alerts Editor',
            component: SecurityAlertsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'security-alerts/new',
            name: 'New Security Alert',
            component: SecurityAlertsEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'log-exporters',
            name: 'Log Exporters',
            redirect: (route) => {
              return `/${route.params.branch}/log-exporters/list`;
            },
          },
          {
            path: 'log-exporters/list',
            name: 'Log Exporters List',
            component: LogExportersList,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'log-exporters/config/:doc_id',
            name: 'Log Exporters Editor',
            component: LogExportersEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'log-exporters/new',
            name: 'New Log Exporter',
            component: LogExportersEditor,
            beforeEnter: [accessLevelGuardEditor],
          },
          {
            path: 'version-control',
            name: 'Version Control',
            beforeEnter: [accessLevelGuardEditor],
            component: VersionControl,
          },
          {
            path: 'system-db',
            name: 'SystemDB',
            redirect: (route) => {
              return `/${route.params.branch}/system-db/list`;
            },
          },
          {
            path: 'system-db/list',
            name: 'System DB List',
            beforeEnter: [accessLevelGuardOrgAdmin],
            component: SystemDBList,
          },
          {
            path: 'system-db/config/:namespace_id',
            name: 'System DB Editor',
            beforeEnter: [accessLevelGuardOrgAdmin],
            component: SystemDBEditor,
          },
          {
            path: 'system-db/new',
            name: 'New System DB',
            beforeEnter: [accessLevelGuardOrgAdmin],
            component: SystemDBEditor,
          },
          {
            path: 'publish',
            name: 'Publish Changes',
            beforeEnter: [accessLevelGuardEditor],
            component: PublishChanges,
          },
          {
            path: 'account',
            name: 'Account',
            beforeEnter: [
              accessLevelGuardEditor,
              () => {
                const userStore = useUserStore();
                if (userStore.getCookieProfileProvider() === 'reblaze' || ENV.DEV) {
                  return true;
                }
                return { path: '/prod/dashboard' };
              },
            ],
            component: AccountDetails,
          },
          {
            path: 'support',
            name: 'Support',
            component: HelpAndSupport,
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(ENV.BASE_URL),
  routes,
});

const toFromQueryMap = {
  Dashboard: ['Events Log', 'AI Management'],
  'Events Log': ['Dashboard', 'AI Management'],
  'AI Management': ['Dashboard', 'Events Log'],
} as GenericObject;

function hasQueryParams(route: RouteLocationNormalized) {
  return !!Object.keys(route.query).length;
}

router.beforeEach((to, from, next) => {
  const branchesStore = useBranchesStore();
  branchesStore.branchIdFromRoute = to.params.branch?.toString();
  if (branchesStore.selectedBranchId !== branchesStore.branchIdFromRoute) {
    branchesStore.setSelectedBranch(branchesStore.branchIdFromRoute);
  }
  if (!toFromQueryMap[to.name as string]?.includes(from.name)) {
    next();
    return;
  }
  if (!hasQueryParams(to) && hasQueryParams(from) && from.query?.filters) {
    next({ name: to.name, query: from.query });
    return;
  } else {
    next();
    return;
  }
});

export { routes };

export default router;
